import { SocketClient } from "cbcore-ts"
import { NO, YES } from "uicore-ts"
import { RHOffererTenderInterestSettings } from "../SocketClientFunctions"
import { RHCore } from "./RHCore"
import { RHFeatureFlags } from "./RHFeatureFlags"
import { RHSubscriptionBase } from "./RHSubscriptionBase"


// ============================================================================
// RHOffererConfigurationStore
//
// Offerer-side company CPV selection and per-user notification choices.
// ============================================================================

export class RHOffererConfigurationStore
    extends RHSubscriptionBase<(settings: RHOffererTenderInterestSettings) => void> {

    readonly _core: RHCore
    _tenderInterestSettings: RHOffererTenderInterestSettings | undefined
    _tenderInterestSettingsGeneration = 0


    constructor(core: RHCore) {
        super()
        this._core = core
    }


    async retrieveTenderInterestSettings(): Promise<RHOffererTenderInterestSettings> {
        if (this._tenderInterestSettings) {
            return this._tenderInterestSettings
        }

        const settingsGeneration = this._tenderInterestSettingsGeneration
        const companyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.RetrieveOffererTenderInterestSettings({ companyId })
        if (settingsGeneration !== this._tenderInterestSettingsGeneration) {
            return this.retrieveTenderInterestSettings()
        }
        if (!response.result) {
            const fallback = "CPV-teavituste laadimine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }

        this._tenderInterestSettings = response.result
        return response.result
    }


    async _activeCompanyIdForExplicitCompanyCalls(): Promise<string | undefined> {
        if (RHFeatureFlags.multiCompanyAffiliationIsEnabled !== YES) {
            return undefined
        }
        return this._core.companyAffiliationStore.activeCompanyIdAfterLoadingAffiliations()
    }


    async saveCompanyTenderInterestCodes(
        additionalCpvCodes: string[]
    ): Promise<RHOffererTenderInterestSettings> {
        const settingsGeneration = ++this._tenderInterestSettingsGeneration
        const companyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.SaveCompanyTenderInterestSettings({
            cpvCodes: additionalCpvCodes,
            usesSystemDefault: NO,
            companyId,
            cpvCodesAreAdditions: YES,
            cpvCodesRepresentExactSelection: YES
        })
        if (!response.result) {
            const fallback = "CPV-teavituste salvestamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }

        if (settingsGeneration === this._tenderInterestSettingsGeneration) {
            this._applyTenderInterestSettings(response.result)
        }
        return response.result
    }


    async restoreSystemDefaultCompanyTenderInterestCodes(): Promise<RHOffererTenderInterestSettings> {
        const settingsGeneration = ++this._tenderInterestSettingsGeneration
        const companyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.SaveCompanyTenderInterestSettings({
            cpvCodes: [],
            usesSystemDefault: YES,
            companyId
        })
        if (!response.result) {
            const fallback = "Ettevõtte lisatud CPV-koodide eemaldamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }

        if (settingsGeneration === this._tenderInterestSettingsGeneration) {
            this._applyTenderInterestSettings(response.result)
        }
        return response.result
    }


    async savePersonalTenderInterestCodes(cpvCodes: string[]): Promise<RHOffererTenderInterestSettings> {
        if (!this._tenderInterestSettings) {
            throw new Error("CPV-teavituste seaded tuleb enne salvestamist laadida.")
        }

        return await this._savePersonalTenderInterestSettings(
            this._tenderInterestSettings.personalIncludesCompanySelection,
            cpvCodes
        )
    }


    async saveCompanySelectionInclusionForPersonalNotifications(
        includesCompanySelection: boolean,
        cpvCodes: string[]
    ): Promise<RHOffererTenderInterestSettings> {
        return await this._savePersonalTenderInterestSettings(
            includesCompanySelection,
            cpvCodes
        )
    }


    async restoreCompanyDefaultTenderInterestCodesForPersonalNotifications():
        Promise<RHOffererTenderInterestSettings> {
        return await this._savePersonalTenderInterestSettings(YES, [])
    }


    async _savePersonalTenderInterestSettings(
        includesCompanySelection: boolean,
        cpvCodes: string[]
    ): Promise<RHOffererTenderInterestSettings> {
        if (!this._tenderInterestSettings) {
            throw new Error("CPV-teavituste seaded tuleb enne salvestamist laadida.")
        }

        const settingsGeneration = ++this._tenderInterestSettingsGeneration
        const companyId = await this._activeCompanyIdForExplicitCompanyCalls()
        const response = await SocketClient.SavePersonalTenderInterestSettings({
            includesCompanySelection,
            cpvCodes,
            companyId,
            cpvCodesRepresentExactSelection: YES
        })
        if (!response.result) {
            const fallback = "Isiklike CPV-teavituste salvestamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }

        if (settingsGeneration === this._tenderInterestSettingsGeneration) {
            this._applyTenderInterestSettings(response.result)
        }
        return response.result
    }


    invalidateTenderInterestSettings() {
        this._tenderInterestSettingsGeneration += 1
        this._tenderInterestSettings = undefined
    }


    /** Resets the cached settings. Call on logout so the next login never sees a stale user's data. */
    clear() {
        this.invalidateTenderInterestSettings()
    }


    _applyTenderInterestSettings(settings: RHOffererTenderInterestSettings) {
        this._tenderInterestSettingsGeneration += 1
        this._tenderInterestSettings = settings
        this._notifySubscribers(settings)
    }

}
